import {
  forOwn,
  floor,
  get,
  has,
  includes,
  isEmpty,
  isNil,
  kebabCase,
  replace,
  round,
  sumBy,
} from 'lodash';
import {
  calculateGrossDollarsShares,
  calculateGrossAmountDollars,
  calculateGrossPercentShares,
} from './typed-funds-utility';
import React, { Fragment } from 'react';
import { isAPP, isRAS } from '../advice/advice-utility';
import { isNewOrReopenIClassFund } from './shared/shared-fund-notes-utility';
import {
  futureYear,
  getCurrentDate,
  getCurrentYear,
} from '../dates/date-utility';
import If from '../../components/Shared/If/If';
import ModalLink from '../../components/Shared/Modal/ModalLink/ModalLink';
import {
  COLLEGE_DISTRIBUTE_FUND_MIN_MAX_MODAL,
  EXCHANGE_FROM_FUND_MIN_MAX_MODAL,
  SELL_ALL_SHARES_CLOSED_TO_NEW_INVESTORS,
  SELL_ALL_SHARES_MODAL,
  SELL_ALL_SHARES_UNCOLLECTED_SHARES,
  SELL_FUND_MIN_MAX_MODAL,
  ROTH_DAILY_MINIMUM_MAXIMUM,
} from '../../redux/modules/modals/modals.module';
import {
  buyLabel,
  exchangeLabel,
  sellLabel,
} from '../../redux/modules/pendingTransactions/pendingTransactions.module';
import {
  AGREEMENT_TYPES,
  COLLEGE_DISTRIBUTE_PAYEE_OPTIONS,
  CORE_CUSTOMER,
  CountryCode,
  DELIVERY_EXPRESS_DELIVERY,
  EXCHANGE,
  EXCHANGE_DEFAULT_MAX,
  EXCHANGE_DEFAULT_MIN,
  EXPRESS_DELIVERY_MIN_TRANSACTION,
  FUND_LIMIT_MESSAGE_DELIMITER,
  NON_FIDUCIARY_PLAN,
  recurringSweepMin,
  RETIREMENT,
  SELECTED_UNIT_ENUM_OBJECT_MAP,
  UNIT,
  specialStates,
  US_STATE_CODE,
  ROTH_CONVERSION_DEFAULT_MIN,
  ROTH_CONVERSION_DEFAULT_MAX,
  RETIREMENT_DATED_FUNDS,
  NEW_ICLASS_FUND_MIN_AMOUNT,
  ProductTypes,
  EXISTING_ICLASS_FUND_MIN_AMOUNT,
} from '../../settings/settings';
import {
  currency,
  currencyNoPrecision,
  customerSegmentBucketType,
  coalesce,
  formatPercent,
  formatShares,
  hasValue,
  isPositiveNumber,
} from '../utils';
import {
  isAppBuy,
  isAppSell,
  isAutoExchange,
  isBuy,
  isCollegeAnything,
  isCollegeDistribute,
  isCollegeExchange,
  isExchange,
  isRecurringBuy,
  isAutomaticSell,
  isRothConversion,
} from '../transactions-util';
import words from '../words';
import {
  DisplayClassification,
  RoleCode,
} from '../../components/Exchange/types';
import { ExchangeTransactionActionType as act } from '../../redux/modules/exchangeTransaction';
import styles from '../../components/Sell/SellFundEditor/DistributionAmount.module.scss';
import qcdBeaconError from '../../assets/images/qcd-beacon-error.png';
import { getRothConversionMinimum } from '../form/validate/validate';
export * from './typed-funds-utility';
export function agreementTypeToDisplay(agreementType) {
  return agreementType === AGREEMENT_TYPES.UGMA_UTMA
    ? 'UGMA/UTMA'
    : typeof agreementType === 'string'
    ? agreementType.replace(/_/g, ' ')
    : '';
}
const productTypes529 = {
  Maryland529: true,
  Alaska529: true,
  National529: true,
  40: true,
  70: true,
  80: true,
};
export function isNewOrReopen(fund) {
  return fund && (fund.newFund || fund?.balanceAsOf === 0);
}

/**
 * {
    "stateCode": "NC",
    "fullDistributionRequired": false,
    "mandatory": true,
    "voluntary": false,
    "restricted": false,
    "percent": 4.00,
    "federalTaxDependent": false,
    "redemptionThreshold": 200.00,
    "w4pRequired": false
  },
 */

export const shouldApplyStateWithholding = (userWithholdingRules, fund) => {
  if (isNil(userWithholdingRules) || isEmpty(userWithholdingRules)) {
    return false;
  }

  if (isNil(fund)) {
    return false;
  }

  const {
    federalTaxDependent,
    fullDistributionRequired,
    mandatory,
    percent,
    redemptionThreshold,
    redemptionTotal,
    restricted,
    stateCode,
    wp4Required,
  } = userWithholdingRules;
  const { closeout, federalWithholdingApplied, grossAmountDollars } = fund;

  if (restricted) {
    return false;
  }

  if (wp4Required) {
    return false;
  }
  if (mandatory) {
    if (stateCode === US_STATE_CODE.AR) {
      return true;
    }
    if (
      stateCode !== US_STATE_CODE.OR &&
      federalTaxDependent &&
      federalWithholdingApplied &&
      redemptionThreshold &&
      (grossAmountDollars || 0) + redemptionTotal >= redemptionThreshold
    ) {
      // NC - considers annual redemptions
      return true;
    }
    if (
      stateCode === US_STATE_CODE.OR &&
      federalTaxDependent &&
      federalWithholdingApplied &&
      redemptionThreshold &&
      (grossAmountDollars || 0) >= redemptionThreshold
    ) {
      // OR
      return true;
    }
    if (
      !federalTaxDependent &&
      !fullDistributionRequired &&
      (grossAmountDollars || 0) >= redemptionThreshold
    ) {
      // CT
      return true;
    }
    if (fullDistributionRequired && closeout) {
      // DC
      return true;
    }
    if (
      percent &&
      percent > 0 &&
      federalTaxDependent &&
      redemptionThreshold === 0 &&
      !fullDistributionRequired
    ) {
      // IA
      return true;
    }
  }
  return false;
};

export const shouldDisableStateWithholding = (userWithholdingRules, fund) => {
  if (isNil(userWithholdingRules) || isEmpty(userWithholdingRules)) {
    return false;
  }

  if (isNil(fund)) {
    return false;
  }

  const { mandatory, stateCode } = userWithholdingRules;

  const { federalWithholdingApplied } = fund;

  const stateDeclinedOnFederalDeclineStates = [
    US_STATE_CODE.CA,
    US_STATE_CODE.DE,
    US_STATE_CODE.IA,
    US_STATE_CODE.KS,
    US_STATE_CODE.ME,
    US_STATE_CODE.MA,
    US_STATE_CODE.NC,
    US_STATE_CODE.OR,
    US_STATE_CODE.VT,
  ];

  return (
    mandatory &&
    !federalWithholdingApplied &&
    includes(stateDeclinedOnFederalDeclineStates, stateCode)
  );
};

/**
 * Check productType by it's string or number code.
 *
 * @param {string|number} productType
 * @returns {boolean}
 */
export const isProductType529 = (productType) => productTypes529[productType];

// Enum converted from com.trp.selfserve.core.model.account.AgreementType.java
export const agreementTypeToProperCaseMapping = {
  INDIVIDUAL: 'Individual',
  JOINT_TENANT: 'Joint Tenant',
  OTHER: 'OTHER',
  COLLEGE_SAVINGS: 'College Savings',

  ROTH_ROLLOVER_IRA: 'Roth Rollover IRA',
  TRADITIONAL_IRA: 'Traditional IRA',
  ROTH_IRA: 'Roth IRA',

  ROLLOVER_IRA: 'Rollover IRA',
  EDUCATION_SAVINGS_ACCOUNT: 'Education Savings Account',
  UGMA_UTMA: 'UGMA_UTMA',

  TRUST: 'Trust',
  CORPORATE: 'Corporate',
  RETIREMENT_PLAN_403: 'Retirement Plan 403',
  RETIREMENT_PLAN_401K: 'Retirement Plan 401K',

  RETIREMENT_PLAN_401K_FUND_REACH: 'Retirement Plan 401K Fund Reach',
  SEP_IRA: 'SEP-IRA',
  SIMPLE_IRA: 'SIMPLE IRA',

  KEOGH: 'Keogh',
  PROFIT_SHARING_PLAN: 'Profit Sharing Plan',
  MONEY_PURCHASE_PENSION_PLAN: 'Money Purchase Pension Plan',

  RETIREMENT_PLAN_457: 'Retirement Plan 457',
  RETIREMENT_PLAN_401A: 'Retirement Plan 401A',
  NON_QUALIFIED_RPS: 'Non Qualified RPS',

  DEFINED_BENEFIT: 'Defined Benefit',
  TRANSFER_ON_DEATH: 'TransferOnDeath',
  NON_PROFIT_ORGANIZATION: 'Non Profit Organization',

  NON_FIDUCIARY_RETIREMENT_PLAN: 'Non Fiduciary Retirement Plan',
  GUARANTEED_INVESTMENT_CONTRACT: 'Guaranteed Investment Contract',

  SAR_SEP: 'SARSEP',
  SIM_401K: 'SIM 401K',
  TRADITIONAL_IRA_DECEASED: 'Traditional IRA Deceased',
  ROTH_IRA_DECEASED: 'Roth IRA Deceased',

  ROTH_IRA_DECEASED_BENE: 'Roth IRA Deceased Bene',
  TRADITIONAL_IRA_DECEASED_BENE: 'Traditional IRA Deceased Bene',

  INDIVIDUAL_401K: 'Individual 401K',
  POWER_OF_ATTORNEY: 'PowerOfAttorney',
  ESTATE: 'Estate',

  RETIREMENT_PLAN_BENE_DUE_TO_DEATH: 'Retirement Plan Bene due to Death',
  RETIREMENT_PLAN_DECEASED: 'Retirement Plan Deceased',

  FIS: 'Intermediary/Institutional',
  ORGANIZATION: 'Organization',
  RETIREMENT_PLAN: 'Retirement Plan',
  JOHN_HANCOCK_GUARDIAN: 'John Hancock Guardian',

  JOHN_HANCOCK_UGMA_UTMA: 'John Hancock UGMA/UTMA',
  JOHN_HANCOCK_TRUST: 'John Hancock Trust',
};

export function agreementTypeToProperCase(agreementTypeOrObjWithAgreementType) {
  const agreementType =
    typeof agreementTypeOrObjWithAgreementType === 'string'
      ? agreementTypeOrObjWithAgreementType
      : agreementTypeOrObjWithAgreementType
      ? agreementTypeOrObjWithAgreementType.agreementType
      : null;

  return coalesce(
    agreementTypeToProperCaseMapping[agreementType],
    agreementType,
    null
  );
}

/**
 * Calculates all derived data for funds within a sell transaction
 *
 * @param {SellFund} fund
 */
export function calculateSellTransactionFundDetails(
  fund,
  userStateWithholdingRules,
  isExchange = false
) {
  const stateRules =
    userStateWithholdingRules?.[fund.address?.state ?? ''] || {};
  if (!fund || !fund.selected) {
    return;
  }
  /*ABCDEF*/
  stateRules.redemptionTotal =
    userStateWithholdingRules?.redemptionTotal?.[fund.address?.state ?? ''] ||
    0;
  const {
    availableShares,
    balanceAsOf,
    closeoutFee,
    displayClassification,
    equityAsOf,
    grossAmount,
    grossUnit,
    sharePrice,
    shares,
    transactionFees,
  } = fund;

  const { percent, stateCode, rounded } = stateRules || {};

  switch (grossUnit) {
    case UNIT.PERCENT:
      fund.netDollarIsEstimate = true;
      break;

    case UNIT.SHARES:
      fund.netDollarIsEstimate = true;
      break;
    default:
      break;
  }

  const grossDollars = fund.grossAmountError
    ? 0
    : calculateGrossAmountDollars(
        isExchange ? balanceAsOf : equityAsOf,
        grossAmount,
        grossUnit,
        sharePrice
      );

  const grossShares = fund.grossAmountError
    ? 0
    : calculateGrossPercentShares(
        isExchange ? shares : availableShares,
        grossAmount,
        grossUnit
      );

  fund.grossAmountDollars = grossDollars;
  fund.grossPercentShares = grossShares;
  let netDollarAmount =
    grossDollars - coalesce(closeoutFee, 0) - coalesce(transactionFees, 0);

  if (displayClassification === RETIREMENT) {
    // fed withholding
    if (isNil(fund.federalWithholdingApplied)) {
      fund.federalWithholdingApplied = true;
    }
    if (isNil(fund.federalWithholdingPercent)) {
      fund.federalWithholdingPercent = 10;
    }
    fund.federalWithholdingDollars = fund.federalWithholdingApplied
      ? Math.round(fund.federalWithholdingPercent * grossDollars) / 100
      : 0;
    // state withholding
    const applyStateWithholding = shouldApplyStateWithholding(stateRules, fund);
    /**
     * this is needed because there are certain states that we will show state withholding
     * but due to the user declined federal withholding from a DST persperctive we need to decline
     * state withholding
     * * */
    const disableStateWithholding = shouldDisableStateWithholding(
      stateRules,
      fund
    );
    const statePercentage = coalesce(percent, 0);
    if (applyStateWithholding && grossUnit === UNIT.DOLLARS) {
      // this is need due to state withholding now being done client side which my differ slightly from DST
      fund.stateIsEstimate = true;
    }
    fund.stateWithholdingApplied = applyStateWithholding;
    fund.stateWithholdingDisabled = disableStateWithholding;
    fund.stateWithholdingPercent = statePercentage;
    const remainingRedemptionThreshold =
      stateRules.redemptionThreshold - stateRules.redemptionTotal ?? 0;
    const eligibleGrossDollars =
      remainingRedemptionThreshold > 0
        ? grossDollars > remainingRedemptionThreshold
          ? grossDollars
          : 0
        : grossDollars;
    fund.federalWithholdingDollars = fund.federalWithholdingApplied
      ? Math.round(fund.federalWithholdingPercent * grossDollars) / 100
      : 0;
    if (specialStates.includes(stateCode)) {
      fund.stateWithholdingDollars = applyStateWithholding
        ? Math.round(
            statePercentage *
              (Math.round(
                fund.federalWithholdingPercent * eligibleGrossDollars
              ) /
                100)
          ) / 100
        : 0;
    } else {
      fund.stateWithholdingDollars = !applyStateWithholding
        ? 0
        : !disableStateWithholding
        ? rounded
          ? Math.round((statePercentage * eligibleGrossDollars) / 100)
          : round((statePercentage / 100) * eligibleGrossDollars, 2)
        : 0;
    }

    netDollarAmount -=
      coalesce(fund.federalWithholdingDollars, 0) +
      coalesce(fund.stateWithholdingDollars, 0);
  }

  fund.netDollarAmount = isPositiveNumber(grossAmount) ? netDollarAmount : 0;
  fund.totalDeductions = isPositiveNumber(grossAmount)
    ? fund.grossAmountDollars - fund.netDollarAmount
    : 0;
  fund.isTakeHomeEstimated =
    fund.stateIsEstimate || fund.netDollarIsEstimate ? true : false;
}

/**
 * Converts current unit amount in fund object to shares
 *
 * @param {object} fund
 * @returns {number} shares
 */
export function convertUnitAmountToShares(fund) {
  const { availableShares, grossAmount, grossUnit, sharePrice } = fund;
  const shares =
    grossUnit === UNIT.DOLLARS
      ? calculateGrossDollarsShares(grossAmount, grossUnit, sharePrice)
      : grossUnit === UNIT.SHARES
      ? grossAmount
      : grossUnit === UNIT.PERCENT
      ? calculateGrossPercentShares(availableShares, grossAmount, grossUnit)
      : 0;
  return shares;
}

/**
 * For any college fund, creates a unique key for the all-funds lookup, which is
 * a hybrid of the source fund's unique key plus the fund name.
 *
 * @param {UserFund|AllFund} fund
 * @param {string?} uniqueKey
 * @returns {string}
 */
export function createCollegeFundUniqueKey(fund, uniqueKey = undefined) {
  return `${coalesce(uniqueKey, fund.uniqueKey)}-${coalesce(
    fund.fundName,
    fund.name
  )}`;
}

// grouping funds
export const getFundsByKeys = (userFunds) => {
  if (userFunds === undefined || userFunds === null) {
    return [];
  }

  const userFund = userFunds[0];
  const allFunds = [];
  const agreementKeys = Object.keys(userFund).filter((key) =>
    key.toLowerCase().includes('agreements')
  );

  agreementKeys.forEach((classification) => {
    const agreementType = userFund[classification];
    if (agreementType === undefined || agreementType === null) {
      return;
    }

    const agreementTypeKeys = Object.keys(agreementType);
    agreementTypeKeys.forEach((agreement) => {
      const funds = agreementType[agreement];
      if (funds !== undefined && funds !== null && funds.length) {
        funds.forEach((fund) => allFunds.push(fund));
      }
    });
  });

  return allFunds;
};

export const getMinimumByType = (fund, type, year) => {
  if (has(fund, year) && !isNil(fund[year])) {
    return fund[year].filter((obj) => {
      return obj.type === type;
    });
  }
  if (
    fund.newFund === true &&
    fund.agreementType === 'ROTH_IRA' &&
    isRothConversion()
  ) {
    return 'PURCHASE';
  }
};

/**
 * @param fund
 * @param {string} year Field to pull minimum from, 'currentYearLimit' or 'priorYearLimit'
 * @returns {number}
 */

export function firstAvailableUncollectedSharesUnixDate(uncollectedShares) {
  if (isEmpty(uncollectedShares)) {
    return null;
  }
  let minDate = uncollectedShares[0].settlementDate;

  uncollectedShares.forEach(({ settlementDate }) => {
    if (settlementDate < minDate) {
      minDate = settlementDate;
    }
  });
  return minDate;
}

export const getFundMinimum = (fund, year) => {
  if (isRecurringBuy() && fund.sweep) {
    return recurringSweepMin;
  }
  if (isAppBuy()) {
    return 100;
  }

  if (fund.agreementType === NON_FIDUCIARY_PLAN && fund.ticker !== 'TRLAX') {
    return 0.01;
  }

  if (fund.sweep) {
    return fund.currentYearLimit.filter((fund) => {
      return fund.type === 'PURCHASE';
    })[0].minimum;
  }

  if (
    isNewOrReopen(fund) &&
    fund.summit &&
    fund.displayClassification === DisplayClassification.NON_RETIREMENT
  ) {
    return fund.minimumInvestmentAmount;
  }

  if (
    isNewOrReopen(fund) &&
    fund.displayClassification === DisplayClassification.NON_RETIREMENT &&
    fund.ticker === 'TRLAX'
  ) {
    return fund.minimumInvestmentAmount;
  }
  if (
    isNewOrReopen(fund) &&
    fund.agreementType === AGREEMENT_TYPES.UGMA_UTMA &&
    fund.displayClassification === DisplayClassification.NON_RETIREMENT
  ) {
    return 1000;
  }

  if (
    isNewOrReopen(fund) &&
    fund.displayClassification === DisplayClassification.NON_RETIREMENT
  ) {
    return fund.minimumInvestmentAmount;
  }

  if (isNewOrReopen(fund) && fund.productType === ProductTypes.NATIONAL529) {
    return 250;
  }

  if (
    isNewOrReopenIClassFund(fund) &&
    fund.displayClassification === DisplayClassification.NON_RETIREMENT
  ) {
    return NEW_ICLASS_FUND_MIN_AMOUNT;
  }

  if (
    fund.iClass &&
    fund.displayClassification === DisplayClassification.RETIREMENT
  ) {
    return EXISTING_ICLASS_FUND_MIN_AMOUNT;
  }
  const transactionType = isRecurringBuy() ? 'AUTOMATICINVESTMENT' : 'PURCHASE';
  const minByType = getMinimumByType(fund, transactionType, year);
  // Todo: Possible removal of code block below before release
  if (isNil(minByType) && minByType?.length < 1) {
    console.log(
      `Using minimum fallback value for ${fund.displayClassification} fund with ticker ${fund.ticker}. Set to $100. `
    );
  }
  return minByType && minByType.length > 0 ? minByType[0].minimum : 100;
};

export const isUserCountryRestricted = (personalData) =>
  personalData &&
  personalData.primaryAddress &&
  personalData.primaryAddress.countryCode !== 'US';

export const isFundCountryRestricted = (fund) =>
  fund && (!fund.address || fund.address?.countryCode in CountryCode);

// checking whether the IRA funds are fully contributed or not
export const checkFullyContributedIRA = (IRALimitdata) => {
  if (
    isNil(IRALimitdata.currentYearRemainingContributions) ||
    isNil(IRALimitdata.withinIRAPriorYearContributionPeriod) ||
    isNil(IRALimitdata.priorYearRemainingContributions)
  ) {
    return false;
  }
  if (IRALimitdata.currentYearRemainingContributions) {
    return false;
  }
  if (!IRALimitdata.withinIRAPriorYearContributionPeriod) {
    return true;
  }
  return IRALimitdata.priorYearRemainingContributions <= 0;
};

// checking whether the client has only Retirement funds or not
export const hasOnlyRetirementFunds = (userFunds) => {
  return (
    userFunds &&
    !userFunds.funds.some((fund) => hasValue(fund.nonRetirementAgreements))
  );
};

/**
 * Monitors the sell fund input, and opens the SELL_ALL_SHARES_MODAL
 * once the value hits a certain threshold for Shares and Percent.
 *
 * @param {number} amount
 * @param {SellFund} fund
 * @param {function} openModal
 * @param {object} transactionLimit
 * @param {function} updateSellFund
 */
export function sellFundInputCloseoutWatcher(
  amount,
  fund,
  openModal,
  transactionLimit,
  updateSellFund
) {
  const {
    agreementID,
    availableShares,
    closeout,
    equityAsOf,
    grossUnit,
    hasUncollectedShares,
    openToNewInvestors,
    redeemableFundDollars,
    sharePrice,
    sweep,
  } = fund;

  const grossAmountDollars = calculateGrossAmountDollars(
    equityAsOf,
    amount,
    grossUnit,
    sharePrice
  );
  let modalName = SELL_ALL_SHARES_MODAL;

  const meetsTransactionLimitCriteria =
    grossAmountDollars < transactionLimit.maximum &&
    grossAmountDollars >= transactionLimit.minimum;

  let inputValueMatchesUnitLimit;

  switch (grossUnit) {
    case UNIT.DOLLARS:
      inputValueMatchesUnitLimit =
        amount === equityAsOf && amount === redeemableFundDollars;
      break;

    case UNIT.PERCENT:
      inputValueMatchesUnitLimit = amount === 100;
      break;

    case UNIT.SHARES:
      inputValueMatchesUnitLimit = amount === availableShares;
      break;

    default:
      inputValueMatchesUnitLimit = false;
      break;
  }

  if (sweep && !hasUncollectedShares && inputValueMatchesUnitLimit) {
    // if sweep and no uncollected shares, we don't show any modal. Update closeout in fund object.
    updateSellFund({
      agreementID,
      closeout: true,
    });
    return;
  }

  // hasUncollectedShares takes precedence as to what modal is presented should fund have both
  if (hasUncollectedShares) {
    modalName = SELL_ALL_SHARES_UNCOLLECTED_SHARES;
  } else if (!openToNewInvestors && !isCollegeDistribute() && !isAppSell()) {
    modalName = SELL_ALL_SHARES_CLOSED_TO_NEW_INVESTORS;
  }

  // Allow modal display only when closeout is undefined or false and when input value entered is
  // 1. Full-equity in dollars or 100% or equal to availableShares and meets min/max daily limit policy.
  // 2. Full-equity in dollars or 100% or equal to availableShares, meets max daily limit policy,
  //    does not have uncollected shares, and is not open to new investors.
  if (!closeout) {
    if (
      (inputValueMatchesUnitLimit && meetsTransactionLimitCriteria) ||
      (inputValueMatchesUnitLimit &&
        grossAmountDollars < transactionLimit.maximum &&
        !hasUncollectedShares)
    ) {
      updateSellFund({
        agreementID,
        closeout: true,
      });
      openModal(modalName, { fund });
    }
  }
}

/**
 * Sets sharePrice value in user fund object mapped to all college fund matching trpCode from user fund object
 *
 * @param {object} fund
 * @param {array} collegeAllFundsMap
 *
 * @returns {void}
 */
export function setCollegeExchangeSharePrice(fund, collegeAllFundsMap) {
  if (
    !(isCollegeExchange() || isCollegeDistribute()) ||
    isNil(collegeAllFundsMap) ||
    collegeAllFundsMap.length < 1
  ) {
    return;
  }

  const { trpCode } = fund;
  const all529Funds = collegeAllFundsMap['all-529-funds'] || [];
  /* eslint-disable */
  all529Funds.map((this529Fund) => {
    const {
      investmentVehicleKey,
      price: { price },
    } = this529Fund;
    if (trpCode === investmentVehicleKey.trpcode) {
      fund.sharePrice = price;
    }
  });
  /* eslint-enable */
}

/**
 * Sets MMR related info in fund object if there are any matching messages for
 * current fund
 *
 * @param {object} fund
 * @param {object} moneyMarketReformFundMessages
 */
export function setMoneyMarketReformFundMessages(
  fund,
  moneyMarketReformFundMessages
) {
  const { trpCode } = fund;
  const hasMMRMessage = has(moneyMarketReformFundMessages, trpCode);
  // Placeholder object to hold key (placeholder),
  // and value (replacement text) that need to be replaced in MMR message.
  const placeholderObj = { _className: 'text-nowrap' };

  if (hasMMRMessage) {
    let mmrMessage = get(
      moneyMarketReformFundMessages,
      `${trpCode}[0].newMessage`
    );
    // Loop through placeholder keys and replace with values in message if found.
    fund.moneyMarketReformMessage = Object.keys(placeholderObj).map((key) => {
      mmrMessage = replace(mmrMessage, key, placeholderObj[key]);
      return mmrMessage;
    })[0];
    fund.moneyMarketReformMessagePeriod = get(
      moneyMarketReformFundMessages,
      `${trpCode}[0].eventPeriodType.enumerationName`
    );
    fund.moneyMarketReformMessageType = get(
      moneyMarketReformFundMessages,
      `${trpCode}[0].messageType.enumerationName`
    );
  }
}

/**
 * Sets pending transaction related info in fund object for matching funds
 *
 * @param fund
 * @param getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped
 */
export function setFundPendingTransaction(
  fund,
  getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped
) {
  const {
    agreementID,
    agreementNumber,
    availableShares,
    balanceAsOf,
    equityAsOf,
    grossAmountDollars,
    grossUnit,
    sharePrice,
  } = fund;

  const fundPendingTransactions = [];
  forOwn(
    getPendingBuyExchangeSellTransactionRecordsByAgreementGrouped,
    (pendingTransactionTypeRecordsByAgreement, type) => {
      if (
        !isNil(pendingTransactionTypeRecordsByAgreement[agreementID]) ||
        !isNil(pendingTransactionTypeRecordsByAgreement[agreementNumber])
      ) {
        fund.hasPendingTransaction = true;
        const key = agreementID || agreementNumber;
        // Pending transaction amount is used for validation during input entry to compute amount that's allowed on the fund.
        // Flag is set to false for flows that should be excluded. Exclude pending transaction from total allowed when pending transaction
        // type is sell or exchange in buy flow, for sell and exchange flows exclude when pending transaction type is buy.
        const excludeTransactionFromTotalAllowed = isBuy()
          ? type === exchangeLabel || type === sellLabel
          : type === buyLabel;
        // Exit loop if flagged for exclusion (Do not include pending transaction for type in array)
        if (excludeTransactionFromTotalAllowed) {
          return;
        }

        fundPendingTransactions.push(
          pendingTransactionTypeRecordsByAgreement[key]
        );
      }
    }
  );

  if (fundPendingTransactions.length < 1) {
    return;
  }

  fund.pendingTransactionAmount = 0;

  // Loop through and set pending transaction dollar amount in fund object
  /* eslint-disable */
  fundPendingTransactions.forEach((fundPendingTransaction) => {
    fund.pendingTransactionAmount += sumBy(
      fundPendingTransaction,
      (pendingTransaction) => {
        // Get pending transaction unit
        const pendingTransactionUnit = pendingTransaction.unit.enumerationName;
        // Compute dollar amount based on share price if unit is in shares, return dollar amount otherwise
        switch (pendingTransactionUnit) {
          case SELECTED_UNIT_ENUM_OBJECT_MAP[UNIT.SHARES].enumerationName:
            return floor(pendingTransaction.shares * sharePrice, 2);
          case SELECTED_UNIT_ENUM_OBJECT_MAP[UNIT.ALL_SHARES].enumerationName:
            return equityAsOf;
          default:
            return pendingTransaction.amount;
        }
      }
    );
  });
  /* eslint-enable */
  // Apply threshold percentage if it exists
  const originalRedeemableFundDollars = fund.redeemableFundDollars;
  if (fund.redeemableThresholdDollarPercentage) {
    fund.redeemableFundDollars =
      equityAsOf > 0 && fund.redeemableThresholdDollarPercentage > 0
        ? floor(
            (fund.redeemableThresholdDollarPercentage / 100) * equityAsOf,
            2
          )
        : 0;

    // Deduct pending transaction amount
    fund.redeemableFundDollars =
      fund.redeemableFundDollars > fund.pendingTransactionAmount
        ? fund.redeemableFundDollars - fund.pendingTransactionAmount
        : 0;
    if (grossAmountDollars) {
      fund.redeemableFundDollars =
        fund.redeemableFundDollars > grossAmountDollars
          ? (fund.redeemableFundDollars -= grossAmountDollars)
          : 0;
    }
    // Exchange uses balanceAsOf rather than equityAsOf and does not take the user entered amount into account
    if (isCollegeExchange() || isExchange() || isAutoExchange()) {
      fund.redeemableFundDollars = originalRedeemableFundDollars;
      fund.redeemableFundDollars =
        balanceAsOf > 0 && fund.redeemableThresholdDollarPercentage > 0
          ? floor(
              (fund.redeemableThresholdDollarPercentage / 100) * balanceAsOf,
              2
            )
          : 0;

      // Deduct pending transaction amount
      fund.redeemableFundDollars =
        fund.redeemableFundDollars > fund.pendingTransactionAmount
          ? fund.redeemableFundDollars - fund.pendingTransactionAmount
          : 0;
    }

    // Set redeemable unit selected in fund object for shares or percent
    if (grossUnit) {
      const pendingTransactionsShares =
        fund.pendingTransactionAmount / sharePrice;
      const redeemableFundShares =
        availableShares > pendingTransactionsShares
          ? floor(availableShares - pendingTransactionsShares, 3)
          : 0;
      /* eslint-disable */
      switch (grossUnit) {
        case UNIT.SHARES:
          fund.redeemableFundShares = redeemableFundShares;
          break;
        // redeemableFundPercent is computed off of redeemableFundShares and availableShares
        case UNIT.PERCENT:
          fund.redeemableFundPercent = floor(
            (redeemableFundShares / availableShares) * 100,
            2
          );
          break;
      }
      /* eslint-enable */
    }
  }
}

/**
 * Constructs and sets min/max limit message as a JSX fragment
 * based on transaction limits, and selected gross unit.
 *
 * @param {object} fund
 */
export function setExchangeFromFundLimitMessage(fund) {
  const {
    balanceAsOf,
    pendingTransactionAmount,
    grossAmount,
    grossAmountDollars,
    grossUnit,
    hasPendingTransaction,
    redeemableFundDollars,
    redeemableFundPercent,
    redeemableFundShares,
    shares,
    ticker,
    transactionLimit,
  } = fund;
  const maxText = 'Daily Total Max:';
  const minText = 'Min:';

  const showPendingTransactionMessage =
    hasPendingTransaction &&
    pendingTransactionAmount > 0 &&
    grossAmountDollars < balanceAsOf;

  const reedemableUnitAmount =
    grossUnit === UNIT.DOLLARS
      ? currency(redeemableFundDollars)
      : grossUnit === UNIT.SHARES
      ? formatShares(redeemableFundShares, words.share)
      : grossUnit === UNIT.PERCENT
      ? formatPercent(redeemableFundPercent)
      : currency(redeemableFundDollars);

  const modalLinkDollars = (
    <span className={`e2e-maximum-limit-${kebabCase(ticker)}`}>
      $<trp-emphasize-unit>{transactionLimit.maximum}</trp-emphasize-unit>
    </span>
  );
  const modalLinkRedeemableUnitAmount = (
    <span className={`e2e-maximum-limit-${kebabCase(ticker)}`}>
      <trp-emphasize-unit>{reedemableUnitAmount}</trp-emphasize-unit>
    </span>
  );
  const limitMessage = (
    <Fragment>
      {minText}{' '}
      <trp-emphasize-unit>
        {currencyNoPrecision(transactionLimit.minimum)}
      </trp-emphasize-unit>{' '}
      {`${FUND_LIMIT_MESSAGE_DELIMITER} ${maxText} `}
      {modalLinkDollars}
      {/*<ModalLink
        link={modalLinkDollars}
        linkStyle="dashed"
        modalName={EXCHANGE_FROM_FUND_MIN_MAX_MODAL}
        modalProps={{ ticker, productType }}
      />*/}
    </Fragment>
  );

  const pendingTransactionMessage = (
    <Fragment>
      <span>Available balance does not reflect pending transactions.</span>
      <br />
      <span>Enter an amount of {modalLinkRedeemableUnitAmount} or less.</span>
    </Fragment>
  );

  switch (grossUnit) {
    case UNIT.SHARES:
      fund.redeemMaxMessage = (
        <Fragment>
          <If test={!showPendingTransactionMessage && grossAmount > shares}>
            <span className="hidden-xs">
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    case UNIT.PERCENT:
      fund.redeemMaxMessage = (
        <Fragment>
          <If test={!showPendingTransactionMessage && grossAmount > 100}>
            <span className="hidden-xs">
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    case UNIT.DOLLARS:
      fund.redeemMaxMessage = (
        <Fragment>
          <If
            test={
              !showPendingTransactionMessage &&
              grossAmount > redeemableFundDollars
            }
          >
            <span className="hidden-xs">
              The most you can exchange from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">Max Daily Fund Exchange:</span>{' '}
            <ModalLink
              link={modalLinkRedeemableUnitAmount}
              linkStyle="dashed"
              modalName={EXCHANGE_FROM_FUND_MIN_MAX_MODAL}
              modalProps={{ productType: fund.productType }}
            />
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    default:
      fund.redeemMaxMessage = '';
  }
  fund.fundLimitMessage = limitMessage;
}

/**
 * Constructs and sets min/max limit message as a JSX fragment
 * based on transaction limits, and selected gross unit.
 *
 * @param {object} fund
 * @param {object} transactionLimit
 */
export function setSellFundLimitMessage(
  customerWebViewEnabled,
  isSalesforceRep,
  fund,
  transactionLimit
) {
  if (!transactionLimit) {
    return fund;
  }
  const {
    availableShares,
    equityAsOf,
    pendingTransactionAmount,
    grossAmount,
    grossAmountDollars,
    grossUnit,
    hasPendingTransaction,
    redeemableFundDollars,
    redeemableFundPercent,
    redeemableFundShares,
    ticker,
  } = fund;
  const maxText = 'Daily Total Max:';
  const minText = 'Min:';

  const showPendingTransactionMessage =
    hasPendingTransaction &&
    pendingTransactionAmount > 0 &&
    grossAmountDollars < equityAsOf;

  const reedemableUnitAmount =
    grossUnit === UNIT.DOLLARS
      ? currency(redeemableFundDollars)
      : grossUnit === UNIT.SHARES
      ? formatShares(redeemableFundShares, words.share)
      : grossUnit === UNIT.PERCENT
      ? formatPercent(redeemableFundPercent)
      : currency(redeemableFundDollars);
  const modalLinkDollars =
    transactionLimit.maximum === Infinity || isRAS(fund.productType) ? (
      <span />
    ) : (
      <span className={`e2e-maximum-limit-${kebabCase(ticker)}`}>
        <trp-emphasize-unit>
          {isCollegeDistribute()
            ? currency(transactionLimit.maximum)
            : currencyNoPrecision(transactionLimit.maximum)}
        </trp-emphasize-unit>
      </span>
    );
  const modalLinkRedeemableUnitAmount = (
    <span className={`e2e-maximum-limit-${kebabCase(ticker)}`}>
      <trp-emphasize-unit>{reedemableUnitAmount}</trp-emphasize-unit>
    </span>
  );
  const limitMessage = (
    <Fragment>
      {minText}{' '}
      <trp-emphasize-unit>
        {currencyNoPrecision(
          (isAutomaticSell() && fund.fundType === 'SUMMIT') ||
            (isAppSell() && isRAS(fund.productType))
            ? 1
            : transactionLimit.minimum
        )}
      </trp-emphasize-unit>
      {transactionLimit.maximum === Infinity ||
      (isAppSell() && isRAS(fund.productType))
        ? ``
        : `${FUND_LIMIT_MESSAGE_DELIMITER} ${maxText}${' '}`}
      {isAutomaticSell() || (!customerWebViewEnabled && isSalesforceRep) ? (
        <> {modalLinkDollars} </>
      ) : (
        <ModalLink
          link={modalLinkDollars}
          linkStyle="dashed"
          modalName={
            isProductType529(fund.productType)
              ? COLLEGE_DISTRIBUTE_FUND_MIN_MAX_MODAL
              : SELL_FUND_MIN_MAX_MODAL
          }
          modalProps={{ ticker, productType: fund.productType }}
        />
      )}
    </Fragment>
  );

  const pendingTransactionMessage = (
    <Fragment>
      <span>Available balance does not reflect pending transactions.</span>
      <br />
      <span>
        Enter an amount of{' '}
        <ModalLink
          link={modalLinkRedeemableUnitAmount}
          linkStyle="dashed"
          modalName={SELL_FUND_MIN_MAX_MODAL}
          modalProps={{ ticker, productType: fund.productType }}
        />{' '}
        or less.
      </span>
    </Fragment>
  );

  switch (grossUnit) {
    case UNIT.SHARES:
      fund.redeemMaxMessage = (
        <Fragment>
          <If
            test={
              !showPendingTransactionMessage && grossAmount > availableShares
            }
          >
            <span className="hidden-xs">
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    case UNIT.PERCENT:
      fund.redeemMaxMessage = (
        <Fragment>
          <If test={!showPendingTransactionMessage && grossAmount > 100}>
            <span className="hidden-xs">
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    case UNIT.DOLLARS:
      fund.redeemMaxMessage = (
        <Fragment>
          <If
            test={
              !showPendingTransactionMessage &&
              grossAmount > redeemableFundDollars
            }
          >
            <span className="hidden-xs">
              The most you can {words.redeem} from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">
              Max Daily Fund Redemption:
            </span>{' '}
            {!isAutomaticSell() ? (
              <ModalLink
                link={modalLinkRedeemableUnitAmount}
                linkStyle="dashed"
                modalName={
                  isProductType529(fund.productType)
                    ? COLLEGE_DISTRIBUTE_FUND_MIN_MAX_MODAL
                    : SELL_FUND_MIN_MAX_MODAL
                }
                modalProps={{ ticker, productType: fund.productType }}
              />
            ) : (
              modalLinkRedeemableUnitAmount
            )}
          </If>
          <If test={showPendingTransactionMessage}>
            {pendingTransactionMessage}
          </If>
        </Fragment>
      );
      break;
    default:
      fund.redeemMaxMessage = '';
  }
  fund.fundLimitMessage = limitMessage;
}

export function setWireFee(customerSegmentName, deliveryMethod, fund) {
  const { grossAmountDollars } = fund;
  const customerSegmentType = customerSegmentBucketType(customerSegmentName);

  fund.wireFee =
    grossAmountDollars &&
    grossAmountDollars < EXPRESS_DELIVERY_MIN_TRANSACTION &&
    customerSegmentType === CORE_CUSTOMER &&
    deliveryMethod === DELIVERY_EXPRESS_DELIVERY;
}

export function isRetirementFund(fund) {
  return (
    fund &&
    fund.agreementType &&
    (fund.agreementType.toUpperCase().endsWith('IRA') ||
      fund.agreementType.toUpperCase().endsWith('IRA_DECEASED_BENE'))
  );
}

export function isSelectedWithAmount({
  balanceAsOf,
  grossAmount,
  grossUnit,
  selected,
}) {
  return (
    selected &&
    (grossAmount ||
      (grossUnit === UNIT.ALL_SHARES && balanceAsOf) ||
      grossUnit === UNIT.ALL_REMAINING)
  );
}

export function isRedeemableFund(fund) {
  if (isNil(fund.fundLock)) {
    return false;
  }
  const {
    fundLock: { fundLocked, missingRequiredFeatures },
  } = fund;
  return fundLocked && missingRequiredFeatures;
}

export function getFundCloseoutAmount(fund, price) {
  if (isNil(fund)) {
    return 0;
  }
  if (fund.availableShares && price) {
    return fund.availableShares * price;
  }
}

export function agreementKey(agreementID, agreementNumber) {
  if (agreementID === null || agreementNumber === null) {
    return '';
  }
  const key = agreementID + '-' + agreementNumber;
  return key;
}

export const collect529Types = (fundList) => {
  const productTypes = fundList.map((fund) => fund.productType);

  return {
    hasAK529: productTypes.includes('Alaska529'),
    hasMD529: productTypes.includes('Maryland529'),
    hasNAT529: productTypes.includes('National529'),
  };
};

export const isProductTypeMutualFunds = (productType) =>
  productType === 'MutualFund';

const exchangeAgreementTypes = {
  IRA: true,
  Regular: true,
  UGMA_AND_UTMA: true,
};

const exchangeFundTypes = {
  IClass: true,
  Regular: true,
  Summit: true,
};

const exchangeOpeningTypes = {
  ExistingAccount: true,
  NewAccount: true,
};

export const isExchangeAgreementType = (value) =>
  value && exchangeAgreementTypes[value.enumerationName];

export const isExchangeFundType = (value) =>
  value && exchangeFundTypes[value.enumerationName];

export const isExchangeOpeningTypes = (value) =>
  value && exchangeOpeningTypes[value.enumerationName];

export const isExchangeLimit = (limitCriteria) =>
  limitCriteria.productType &&
  limitCriteria.productType.enumerationName === 'MutualFund' &&
  limitCriteria.transactionType &&
  limitCriteria.transactionType.enumerationName === 'Exchange';

/**
 * @typedef {object} TransactionLimits
 * @property {string} type
 * @property {} maximum
 * @property {} minimum
 */

/**
 * Gets transaction limits from service and replaces null values with defaults from the settings file
 * @param {TransactionLimits} currentYearLimit
 * @returns {object}
 */
export const getExchangeFromFundLimits = (currentYearLimit) => {
  const { maximum, minimum } =
    currentYearLimit.find((item) => item.type === EXCHANGE) || {};
  return {
    maximum: maximum || EXCHANGE_DEFAULT_MAX,
    minimum: minimum || EXCHANGE_DEFAULT_MIN,
  };
};

export const getAutoExchangeFromFundLimits = () => ({
  maximum: 25000,
  minimum: 100,
});

export function exchangeFundInputCloseoutWatcher(
  amount,
  fund,
  openModal,
  updateExchangeFund
) {
  const {
    agreementID,
    closeout,
    equityAsOf,
    grossUnit,
    redeemableFundDollars,
    sharePrice,
    shares,
    transactionLimit,
    sweep,
    hasUncollectedShares,
  } = fund;
  let { openToNewInvestors } = fund;

  const grossAmountDollars = calculateGrossAmountDollars(
    equityAsOf,
    amount,
    grossUnit,
    sharePrice
  );

  const meetsTransactionLimitCriteria =
    grossAmountDollars < transactionLimit.maximum &&
    grossAmountDollars >= transactionLimit.minimum;

  let inputValueMatchesUnitLimit;
  let modalName = SELL_ALL_SHARES_MODAL;

  // 529 portfolios are all open to new investors.
  // Do not present closed to new investors modal,
  // set 'openToNewInvestors' flag to true
  if (isCollegeAnything() || isAPP(fund.productType)) {
    openToNewInvestors = true;
  }

  if (!openToNewInvestors) {
    modalName = SELL_ALL_SHARES_CLOSED_TO_NEW_INVESTORS;
  }

  switch (grossUnit) {
    case UNIT.DOLLARS:
      inputValueMatchesUnitLimit =
        amount === equityAsOf && amount === redeemableFundDollars;
      break;

    case UNIT.PERCENT:
      inputValueMatchesUnitLimit = amount === 100;
      break;

    case UNIT.SHARES:
      inputValueMatchesUnitLimit = amount === shares;
      break;

    default:
      inputValueMatchesUnitLimit = false;
      break;
  }

  if (sweep && !hasUncollectedShares && inputValueMatchesUnitLimit) {
    // if sweep and no uncollected shares, we don't show any modal. Update closeout in fund object.
    updateExchangeFund(act.UPDATE_EXCHANGE_FROM_FUND, {
      agreementID,
      closeout: true,
    });
    return;
  }

  // Allow modal display only when closeout is undefined or false and when input value entered is
  // 1. Full-equity in dollars or 100% or equal to shares and meets min/max daily limit policy.
  // 2. Full-equity in dollars or 100% or equal to shares, meets max daily limit policy.
  if (!closeout) {
    if (
      (inputValueMatchesUnitLimit && meetsTransactionLimitCriteria) ||
      (inputValueMatchesUnitLimit &&
        grossAmountDollars < transactionLimit.maximum)
    ) {
      updateExchangeFund('UPDATE_EXCHANGE_FROM_FUND', {
        agreementID,
        closeout: true,
      });
      openModal(modalName, { fund });
    }
  }
}

/***
 * Callback for Fund's input change for Roth Conversion Flow
 * Will watch to see if user's inputs can closeout the fund
 */
export function rothConversionFundInputCloseoutWatcher(
  amount,
  fund,
  openModal,
  updateConversionFund
) {
  const {
    closeout,
    equityAsOf,
    grossUnit,
    sharePrice,
    shares,
    transactionLimit,
    sweep,
    hasUncollectedShares,
  } = fund;
  let { openToNewInvestors } = fund;

  const grossAmountDollars = calculateGrossAmountDollars(
    equityAsOf,
    amount,
    grossUnit,
    sharePrice
  );

  const meetsTransactionLimitCriteria =
    grossAmountDollars < transactionLimit.maximum &&
    grossAmountDollars >= transactionLimit.minimum;

  let inputValueMatchesUnitLimit;
  let modalName = SELL_ALL_SHARES_MODAL;

  // 529 portfolios are all open to new investors.
  // Do not present closed to new investors modal,
  // set 'openToNewInvestors' flag to true
  if (isCollegeAnything() || isAPP(fund.productType)) {
    openToNewInvestors = true;
  }

  if (!openToNewInvestors) {
    modalName = SELL_ALL_SHARES_CLOSED_TO_NEW_INVESTORS;
  }

  switch (grossUnit) {
    case UNIT.DOLLARS:
      inputValueMatchesUnitLimit = amount === equityAsOf;
      break;

    case UNIT.PERCENT:
      inputValueMatchesUnitLimit = amount === 100;
      break;

    case UNIT.SHARES:
      inputValueMatchesUnitLimit = amount === shares;
      break;

    default:
      inputValueMatchesUnitLimit = false;
      break;
  }

  if (sweep && !hasUncollectedShares && inputValueMatchesUnitLimit) {
    // if sweep and no uncollected shares, we don't show any modal. Update closeout in fund object.
    updateConversionFund(fund, {
      customProps: { closeout: true },
    });
    return;
  }

  // Allow modal display only when closeout is undefined or false and when input value entered is
  // 1. Full-equity in dollars or 100% or equal to shares and meets min/max daily limit policy.
  // 2. Full-equity in dollars or 100% or equal to shares, meets max daily limit policy.
  if (!closeout) {
    if (
      (inputValueMatchesUnitLimit && meetsTransactionLimitCriteria) ||
      (inputValueMatchesUnitLimit &&
        grossAmountDollars < transactionLimit.maximum)
    ) {
      updateConversionFund(fund, {
        customProps: { closeout: true },
      });
      openModal(modalName, { fund });
    }
  }
}

export function getMonthYear(month, fund) {
  const currDate = getCurrentDate();
  const currMonth = currDate.format('M') - 1;
  const currDay = currDate.format('DD');
  /* eslint-disable */
  return month < currMonth ||
    (month === currMonth &&
      currDay > fund.transferDays[fund.transferDays.length - 1])
    ? futureYear(1, 'YYYY')
    : getCurrentYear('YYYY');
  /* eslint-enable */
}

/**
 * Function to determine which agreement party to mail check to in sell review step
 */
export const mailCheckToNamePicker = (fund) => {
  let payCheckTo;
  const { agreementParties, payee } = fund;
  if (payee === COLLEGE_DISTRIBUTE_PAYEE_OPTIONS.BENEFICIARY) {
    const { fullName } = agreementParties
      ? agreementParties.find(
          (ele) => ele.primaryParty === 'N' && ele.roleCode === RoleCode.MINOR
        )
      : {};
    payCheckTo = fullName;
  } else {
    const { fullName } = agreementParties
      ? agreementParties.find((ele) => ele.primaryParty === 'Y')
      : {};
    payCheckTo = fullName;
  }
  return payCheckTo;
};

export const hiddenInvestmentCount = (
  isSalesforceRep,
  customerWebViewEnabled,
  funds
) =>
  funds.filter(
    (fund) =>
      (
        fund.viewOnly ||
        (fund.hiddenFromOnlineCustomer &&
          !(!customerWebViewEnabled && isSalesforceRep))
      ).length
  );

/**
 * Min/Max message for roth conversion funds
 * @param {Fund} fund
 * @param {Fund[]} existingRothIRAFunds
 * @returns {Component}
 */
export function getRothConversionFundLimitMessage(
  fund,
  existingRothIRAFunds = []
) {
  const { ticker } = fund;
  const ROTH_CONVERSION_MINIMUM = getRothConversionMinimum(
    fund,
    existingRothIRAFunds
  );
  return (
    <Fragment>
      Min:{' '}
      <trp-emphasize-unit>
        {currency(ROTH_CONVERSION_MINIMUM)}
      </trp-emphasize-unit>{' '}
      {`${FUND_LIMIT_MESSAGE_DELIMITER} Max: `}
      <span className={`e2e-maximum-limit-${kebabCase(ticker)}`}>
        <trp-emphasize-unit>
          {currencyNoPrecision(ROTH_CONVERSION_DEFAULT_MAX)}
        </trp-emphasize-unit>
      </span>
    </Fragment>
  );
}

export const getMismatchBusinessParty = (fund, personalData) => {
  return (fund.agreementParties || []).find(({ roleCode, businessPartyID }) => {
    return (
      roleCode === 'OWNR' && businessPartyID !== personalData.businessPartyId
    );
  });
};

/**
 * Constructs and sets min/max limit message as a JSX fragment
 * based on transaction limits, and selected gross unit.
 *
 * @param {object} fund
 * @param {object} transactionLimit
 */
export function getRothFundValidationMessage(fund) {
  const {
    availableShares,
    redeemableFundDollars,
    grossAmount,
    grossUnit,
    transactionLimit,
    redeemableThresholdDollarPercentage,
    grossAmountDollars,
  } = fund;
  switch (grossUnit) {
    case UNIT.ALL_SHARES:
      return (
        <Fragment>
          <If test={grossAmount > availableShares}>
            <span>
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If
            test={
              grossAmountDollars > transactionLimit.maximum &&
              grossAmountDollars <= redeemableFundDollars
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">
              Max Daily Fund Redemption:
            </span>{' '}
            <ModalLink
              link={currency(transactionLimit.maximum)}
              linkStyle={'underline'}
              modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
              error={true}
            />
          </If>
        </Fragment>
      );
    case UNIT.SHARES:
      return (
        <Fragment>
          <If test={grossAmount > availableShares}>
            <span>
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
          </If>
          <If
            test={
              grossAmount >
                availableShares * (redeemableThresholdDollarPercentage / 100) &&
              grossAmountDollars <= transactionLimit.maximum &&
              grossAmount <= availableShares
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is{' '}
              {availableShares * redeemableThresholdDollarPercentage} shares
            </span>
          </If>
          <If
            test={
              grossAmountDollars > transactionLimit.maximum &&
              grossAmount <= availableShares
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">
              Max Daily Fund Redemption:
            </span>{' '}
            <ModalLink
              link={currency(transactionLimit.maximum)}
              linkStyle={'underline'}
              modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
              error={true}
            />
          </If>
        </Fragment>
      );
    case UNIT.PERCENT:
      return (
        <Fragment>
          <If
            test={
              grossAmount > 100 && grossAmountDollars > redeemableFundDollars
            }
          >
            <span>
              You have entered an amount greater than the total of your{' '}
              {words.shares}.
            </span>
            <span className="visible-xs-inline">
              Amount entered is greater than total {words.shares}
            </span>
          </If>
          <If
            test={
              grossAmount > redeemableThresholdDollarPercentage &&
              grossAmountDollars <= transactionLimit.maximum &&
              grossAmount < 100
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is{' '}
              {redeemableThresholdDollarPercentage}%
            </span>
          </If>
          <If
            test={
              grossAmountDollars > transactionLimit.maximum &&
              grossAmount <= 100
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">
              Max Daily Fund Redemption:
            </span>{' '}
            <ModalLink
              link={currency(transactionLimit.maximum)}
              linkStyle={'underline'}
              modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
              error={true}
            />
          </If>
        </Fragment>
      );
    case UNIT.DOLLARS:
      return (
        <Fragment>
          <If test={grossAmount < transactionLimit.minimum}>
            <ModalLink
              link={fund.fundLimitMessage}
              linkStyle={'dashed'}
              modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
              error={true}
            />
          </If>
          <If
            test={
              grossAmountDollars > transactionLimit.maximum &&
              grossAmountDollars <= redeemableFundDollars
            }
          >
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is
            </span>
            <span className="visible-xs-inline">
              Max Daily Fund Redemption:
            </span>{' '}
            <ModalLink
              link={currency(transactionLimit.maximum)}
              linkStyle={'underline'}
              modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
              error={true}
            />
          </If>
          <If test={grossAmountDollars > redeemableFundDollars}>
            <span className="roth-conversion-inline-error">
              The most you can {words.redeem} from this {words.fund} today is{' '}
              <ModalLink
                link={currency(redeemableFundDollars)}
                linkStyle={'underline'}
                modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
                error={true}
              />
            </span>
          </If>
        </Fragment>
      );
    default:
      return '';
  }
}

/**
 * Constructs and sets min/max limit message as a JSX fragment
 * based on transaction limits, and selected gross unit.
 *
 * @param {object} fund
 * @param {object} transactionLimit
 */
export function getRothIRAFundValidationMessage(
  fund,
  sumAllFunds,
  totalAllocated
) {
  const { grossAmountDollars } = fund;

  return (
    <Fragment>
      <If
        test={
          grossAmountDollars < ROTH_CONVERSION_DEFAULT_MIN ||
          grossAmountDollars > ROTH_CONVERSION_DEFAULT_MAX
        }
      >
        <ModalLink
          link={fund.fundLimitMessage}
          linkStyle={'dashed'}
          modalName={ROTH_DAILY_MINIMUM_MAXIMUM}
          error={true}
        />
      </If>
      <If test={sumAllFunds !== totalAllocated}>
        <p className="roth-conversion-inline-error">
          Please adjust your allocation amounts so that they total{' '}
          <span className="roth-conversion-inline-error_total">
            {currency(totalAllocated)}
          </span>{' '}
          or edit your original allocation amount
        </p>
      </If>
    </Fragment>
  );
}

/* Checks to see if a fund is a retirement dated fund
 * @param {string} ticker
 * @return {boolean}
 */
export const isRetirementDatedFund = (ticker) => {
  return RETIREMENT_DATED_FUNDS[ticker];
};

/**
 * Determines if a sell fund has uncollected shares excluding RAS funds, which have inaccurate equityAsOf values
 * @param {number} balanceAsOf
 * @param {number} equityAsOf
 * @param {string} productType
 * @return {boolean}
 */
export const getHasUncollectedShares = (
  balanceAsOf,
  equityAsOf,
  productType
) => {
  return balanceAsOf !== equityAsOf && !isRAS(productType);
};

// current date
const date = new Date();
date.setDate(date.getDate() - 15);
/**
 * Determines if a sell fund has an unavailable delivery method, which is a fund with an effective date less than 15 days
 * @param {object} address
 */
export const getHasUnavailableDelivery = (address) => {
  return !(new Date(address.effectiveDate) < date);
};
export function setQCDFundLimitMessage(
  fund,
  totalGrossAmountAgreements,
  totalGrossAmountCharity
) {
  if (
    fund.pendingTransactionAmount > 0 &&
    fund.redeemableFundDollars - fund.pendingTransactionAmount <
      fund.grossAmountDollars
  ) {
    fund.QCDgrossAmountError = (
      <span className={styles['qcd-error-distribution']}>
        <div>
          <img
            alt="qcdBeaconError"
            className={styles['qcd-error-img']}
            src={qcdBeaconError}
          />{' '}
        </div>
        <span className="hidden-xs qcdError-text">
          The most you can {words.redeem} from this {words.fund} today is{' '}
          {currency(fund.redeemableFundDollars - fund.pendingTransactionAmount)}
        </span>
        <span className="visible-xs-inline qcdError-text">
          Max Daily Fund Redemption:{' '}
          {currency(fund.redeemableFundDollars - fund.pendingTransactionAmount)}
        </span>
      </span>
    );
  } else if (fund.redeemableFundDollars < fund.grossAmountDollars) {
    fund.QCDgrossAmountError = (
      <span className={styles['qcd-error-distribution']}>
        <div>
          <img
            alt="qcdBeaconError"
            className={styles['qcd-error-img']}
            src={qcdBeaconError}
          />{' '}
        </div>
        <span className="hidden-xs qcdError-text">
          The most you can {words.redeem} from this {words.fund} today is{' '}
          {currency(fund.redeemableFundDollars)}
        </span>
        <span className="visible-xs-inline qcdError-text">
          Max Daily Fund Redemption: {currency(fund.redeemableFundDollars)}
        </span>
      </span>
    );
  } else if (fund.grossAmountDollars < 1) {
    fund.QCDgrossAmountError = (
      <span className={styles['qcd-error-distribution']}>
        <div>
          <img
            alt="qcdBeaconError"
            className={styles['qcd-error-img']}
            src={qcdBeaconError}
          />{' '}
        </div>
        <span className="qcdError-text_no_padding">
          Please set your distribution amount to more than $1
        </span>
      </span>
    );
  } else if (totalGrossAmountCharity !== totalGrossAmountAgreements) {
    fund.QCDgrossAmountError = (
      <span className={styles['qcd-error-distribution']}>
        <div>
          <img
            alt="qcdBeaconError"
            className={styles['qcd-error-img']}
            src={qcdBeaconError}
          />{' '}
        </div>
        <span className="qcdError-text_no_padding">
          Please set your distribution amounts to total{' '}
          {currency(totalGrossAmountCharity)} or adjust your original amount.
        </span>
      </span>
    );
  } else {
    fund.QCDgrossAmountError = undefined;
  }
}
